import { useState } from "react";
import { FaTimes, FaCheck, FaClock } from "react-icons/fa";

const AccessTypeModal = ({ isOpen, onClose, onConfirm, requestId }) => {
  const [accessType, setAccessType] = useState("unlimited");
  const [expiryValue, setExpiryValue] = useState(1);
  const [expiryUnit, setExpiryUnit] = useState("hours");

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (accessType === "limited" && (!expiryValue || expiryValue <= 0)) {
      return; // Validation handled by backend, but we can add frontend validation
    }
    onConfirm(accessType, accessType === "limited" ? expiryValue : null, accessType === "limited" ? expiryUnit : null);
    // Reset form
    setAccessType("unlimited");
    setExpiryValue(1);
    setExpiryUnit("hours");
  };

  const handleCancel = () => {
    // Reset form
    setAccessType("unlimited");
    setExpiryValue(1);
    setExpiryUnit("hours");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FaClock className="text-primary-600 text-lg" />
            <h3 className="text-lg font-semibold text-gray-800 font-heading">
              Set Access Type
            </h3>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Access Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3 font-heading">
              Access Type
            </label>
            <div className="space-y-2">
              <button
                onClick={() => setAccessType("unlimited")}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  accessType === "unlimited"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800 font-heading">
                      Unlimited Access
                    </div>
                    <div className="text-sm text-gray-600 font-roboto-flex mt-1">
                      User can view your profile indefinitely
                    </div>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      accessType === "unlimited"
                        ? "border-primary-500 bg-primary-500"
                        : "border-gray-300"
                    }`}
                  >
                    {accessType === "unlimited" && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                </div>
              </button>

              <button
                onClick={() => setAccessType("limited")}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  accessType === "limited"
                    ? "border-primary-500 bg-primary-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-800 font-heading">
                      Limited Access
                    </div>
                    <div className="text-sm text-gray-600 font-roboto-flex mt-1">
                      Set a time limit for profile access
                    </div>
                  </div>
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      accessType === "limited"
                        ? "border-primary-500 bg-primary-500"
                        : "border-gray-300"
                    }`}
                  >
                    {accessType === "limited" && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Time Settings (only shown for limited access) */}
          {accessType === "limited" && (
            <div className="space-y-4 pt-2 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 font-heading">
                Set Time Limit
              </label>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <input
                    type="number"
                    min="1"
                    value={expiryValue}
                    onChange={(e) => setExpiryValue(parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-roboto-flex"
                    placeholder="Enter time"
                  />
                </div>
                <div className="w-32">
                  <select
                    value={expiryUnit}
                    onChange={(e) => setExpiryUnit(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white font-roboto-flex"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>
              {expiryValue <= 0 && (
                <p className="text-xs text-red-600 font-roboto-flex">
                  Time value must be greater than 0
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-roboto-flex"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={accessType === "limited" && expiryValue <= 0}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-roboto-flex"
          >
            <FaCheck className="w-3 h-3" />
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccessTypeModal;
